import { AnnouncementDTO } from '@pya-mms/common';
import axios from 'axios';
import Link from 'next/link';
import { FC, useCallback, useEffect, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from '../ui/Icon/reactIcons';
import SignedImage from '../AWS';
import { Button, Modal, Text } from '../ui';
import { FlexContainer } from '../ui/Container';
import MarkdownContent from '../ui/MarkdownContent/MarkdownContent';
import { DateToString } from '../../utils/formatters';

const SESSION_KEY = 'whats-new-popup-shown';

const SUPPRESSED_PATH_PREFIXES = ['/login', '/register', '/reset-password', '/email-verify', '/membership/payment'];

type AnnouncementsModalProps = {
  enabled: boolean;
  isActive: boolean;
  isBanned: boolean;
  pathname: string;
};

const AnnouncementsModal: FC<AnnouncementsModalProps> = ({ enabled, isActive, isBanned, pathname }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<AnnouncementDTO[]>([]);
  const [index, setIndex] = useState(0);

  const isSuppressedRoute = SUPPRESSED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  const markSeen = useCallback(async (items: AnnouncementDTO[]) => {
    if (!items.length) return;
    await axios.post('/announcements/seen', {
      announcementIds: items.map((item) => item._id),
    });
  }, []);

  const closeModal = useCallback(async () => {
    await markSeen(announcements);
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_KEY, '1');
    }
  }, [announcements, markSeen]);

  useEffect(() => {
    if (!enabled || !isActive || isBanned || isSuppressedRoute) return;
    if (typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY)) return;

    const loadUnseen = async () => {
      try {
        const { data } = await axios.get('/announcements/unseen');
        const items: AnnouncementDTO[] = data?.data ?? [];
        if (items.length) {
          setAnnouncements(items);
          setIndex(0);
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Failed to load unseen announcements', error);
      }
    };

    loadUnseen();
  }, [enabled, isActive, isBanned, isSuppressedRoute]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        closeModal();
      }
      if (event.key === 'ArrowRight') {
        setIndex((prev) => Math.min(prev + 1, announcements.length - 1));
      }
      if (event.key === 'ArrowLeft') {
        setIndex((prev) => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [announcements.length, closeModal, isOpen]);

  if (!isOpen || !announcements.length) return null;

  const current = announcements[index];

  return (
    <Modal title="What's New" isOpen={isOpen} close={closeModal} maxWidth="md">
      <FlexContainer flexDirection="column" gap="sm">
        {current.coverImageKey && (
          <div style={{ maxHeight: 220, overflow: 'hidden', borderRadius: 8 }}>
            <SignedImage src={current.coverImageKey} alt={current.title} objectFit="cover" />
          </div>
        )}
        <Text weight="bold" size="md">
          {current.title}
        </Text>
        {current.publishedAt && (
          <Text size="xs" color="textSecondary">
            {DateToString(new Date(current.publishedAt))}
          </Text>
        )}
        <Text size="sm">{current.summary}</Text>
        <MarkdownContent content={current.body} />
        <FlexContainer justify="space-between" align="center">
          <FlexContainer gap="xs" align="center">
            {announcements.map((item, dotIndex) => (
              <button
                key={item._id}
                type="button"
                onClick={() => setIndex(dotIndex)}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  border: 'none',
                  background: dotIndex === index ? '#0B5FD9' : '#d1d5db',
                  cursor: 'pointer',
                }}
                aria-label={`Go to slide ${dotIndex + 1}`}
              />
            ))}
          </FlexContainer>
          <FlexContainer gap="xs">
            <Button
              size="sm"
              color="white"
              textColor="text"
              style={{ border: '1px solid #E9EAF3' }}
              disabled={index === 0}
              onClick={() => setIndex((prev) => Math.max(prev - 1, 0))}
            >
              <FaChevronLeft size={12} style={{ marginRight: 6 }} />
              Previous
            </Button>
            <Button
              size="sm"
              color="white"
              textColor="text"
              style={{ border: '1px solid #E9EAF3' }}
              disabled={index === announcements.length - 1}
              onClick={() => setIndex((prev) => Math.min(prev + 1, announcements.length - 1))}
            >
              Next
              <FaChevronRight size={12} style={{ marginLeft: 6 }} />
            </Button>
          </FlexContainer>
        </FlexContainer>
        <FlexContainer justify="space-between" align="center">
          <Link href="/whats-new">
            <a onClick={closeModal}>See all announcements</a>
          </Link>
          <Button onClick={closeModal}>Close</Button>
        </FlexContainer>
      </FlexContainer>
    </Modal>
  );
};

export default AnnouncementsModal;
